import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc as docRef,
  getDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { db } from "../../Firebase/firebaseconfig";
import * as agendaCobranza from "../../helpers/agendaCobranza";
import { calcularPerfilCobranzaV2 } from "../../helpers/cobranzaInteligenteV2";

import "./AgendaVencida.css";
import notify from "../common/notify";

const AgendaVencida = () => {
  const navigate = useNavigate();

  const clientesCache = useRef(new Map());
  const fraccionamientosCache = useRef(new Map());

  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtroRiesgo, setFiltroRiesgo] = useState("todos");
  const [filtroCartera, setFiltroCartera] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [fraccionamiento, setFraccionamiento] = useState("todos");
  const [orden, setOrden] = useState("meses");

  const getCliente = useCallback(async (clienteId) => {
    if (!clienteId) return {};

    if (clientesCache.current.has(clienteId)) {
      return clientesCache.current.get(clienteId);
    }

    const snap = await getDoc(docRef(db, "clientes", clienteId));
    const data = snap.exists() ? snap.data() : {};
    clientesCache.current.set(clienteId, data);
    return data;
  }, []);

  const getFraccionamientoNombre = useCallback(async (dataDeuda) => {
    const fraccId = agendaCobranza.getFraccionamientoIdFromDeuda(dataDeuda);

    if (!fraccId) {
      return agendaCobranza.getFraccionamientoNombreFallback(dataDeuda);
    }

    if (fraccionamientosCache.current.has(fraccId)) {
      return fraccionamientosCache.current.get(fraccId);
    }

    const snap = await getDoc(docRef(db, "fraccionamientos", fraccId));
    const nombre = snap.exists()
      ? snap.data()?.nombre || "—"
      : agendaCobranza.getFraccionamientoNombreFallback(dataDeuda);

    fraccionamientosCache.current.set(fraccId, nombre);
    return nombre;
  }, []);

  const enriquecer = useCallback(
    async (docSnap) => {
      const data = docSnap.data();

      const cliente = await getCliente(data?.clienteId);

      let responsableNombre = "";
      if (data?.fueraPaisRegularmente && data?.responsableId) {
        const responsable = await getCliente(data.responsableId);
        responsableNombre = agendaCobranza.getNombreCompleto(responsable);
        if (responsableNombre === "Sin nombre") responsableNombre = "";
      }

      const nextDueDate = agendaCobranza.getDueTimestamp(data);
      const saldoRestante = agendaCobranza.calcularSaldoMostrable(data);
      const mensualidad = agendaCobranza.getMensualidadMostrable(data);
      const perfilCobranza = calcularPerfilCobranzaV2(data);
      const mesesAtraso = Number(perfilCobranza.mesesAtraso || 0);
      const saldoAtrasado = Number(perfilCobranza.saldoAtrasado || 0);
      const riesgo = agendaCobranza.getRiesgoAtrasoPorMeses(mesesAtraso);
      const categoria = perfilCobranza.cartera || "AL_CORRIENTE";
      const categoriaDescripcion = perfilCobranza.carteraDescripcion || "Sin atraso";
      const fraccionamientoNombre = await getFraccionamientoNombre(data);

      return {
        id: docSnap.id,
        clienteId: data?.clienteId || "",
        cliente: agendaCobranza.getNombreCompleto(cliente),
        responsableNombre,
        telefono: cliente?.telefono || "—",
        direccion: cliente?.direccion || "—",
        referencia: data?.referencia || "—",
        fraccionamiento: fraccionamientoNombre || "—",
        saldoRestante,
        mensualidad,
        nextDueDate,
        mesesAtraso,
        saldoAtrasado,
        perfilCobranza,
        riesgo,
        categoria,
        categoriaDescripcion,
        estado: data?.estado || data?.estatus || "—",
      };
    },
    [getCliente, getFraccionamientoNombre]
  );

  useEffect(() => {
    const q = query(collection(db, "deudas"), where("activo", "==", true));

    const unsub = onSnapshot(
      q,
      async (snap) => {
        try {
          setLoading(true);

          const docs = await Promise.all(snap.docs.map(enriquecer));

          const vencidas = docs.filter((item) => {
            const due = item.nextDueDate?.toDate?.();
            if (!due) return false;
            if (Number(item.saldoRestante || 0) <= 0) return false;
            if (Number(item.mesesAtraso || 0) <= 0) return false;
            return true;
          });

          setRegistros(vencidas);
        } catch (error) {
          console.error("Error cargando agenda vencida:", error);
          setRegistros([]);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error en agenda vencida:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [enriquecer]);

  const handleVerDetalle = useCallback(
    (deuda) => {
      navigate(`/app/cliente/${deuda.clienteId}/deudas/${deuda.id}`);
    },
    [navigate]
  );

  const fraccionamientos = useMemo(() => {
    const unique = [...new Set(registros.map((r) => r.fraccionamiento).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b, "es"));
  }, [registros]);

  const resumen = useMemo(() => {
    const alto = registros.filter((r) => r.riesgo === "alto");
    const medio = registros.filter((r) => r.riesgo === "medio");
    const bajo = registros.filter((r) => r.riesgo === "bajo");

    const carteraA = registros.filter((r) => r.categoria === "A");
    const carteraB = registros.filter((r) => r.categoria === "B");
    const carteraC = registros.filter((r) => r.categoria === "C");
    const carteraD = registros.filter((r) => r.categoria === "D");
    const carteraColaFinal = registros.filter((r) => r.categoria === "COLA_FINAL");

    return {
      altoCount: alto.length,
      medioCount: medio.length,
      bajoCount: bajo.length,

      carteraACount: carteraA.length,
      carteraBCount: carteraB.length,
      carteraCCount: carteraC.length,
      carteraDCount: carteraD.length,
      carteraColaFinalCount: carteraColaFinal.length,

      total: registros.length,
      saldoRestanteTotal: registros.reduce(
        (acc, item) => acc + Number(item.saldoRestante || 0),
        0
      ),
      saldoAtrasadoTotal: registros.reduce(
        (acc, item) => acc + Number(item.saldoAtrasado || 0),
        0
      ),
    };
  }, [registros]);

  const registrosFiltrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();

    const filtered = registros.filter((r) => {
      const byRiesgo = filtroRiesgo === "todos" ? true : r.riesgo === filtroRiesgo;
      const byCartera = filtroCartera === "todos" ? true : r.categoria === filtroCartera;
      const byFracc = fraccionamiento === "todos" ? true : r.fraccionamiento === fraccionamiento;
      const bySearch = !term || agendaCobranza.getTextoBusquedaAgenda(r).includes(term);

      return byRiesgo && byCartera && byFracc && bySearch;
    });

    filtered.sort((a, b) => {
      if (orden === "cliente") {
        return String(a.cliente || "").localeCompare(String(b.cliente || ""), "es");
      }

      if (orden === "saldoAtrasado") {
        return Number(b.saldoAtrasado || 0) - Number(a.saldoAtrasado || 0);
      }

      if (orden === "saldoRestante") {
        return Number(b.saldoRestante || 0) - Number(a.saldoRestante || 0);
      }

      if (orden === "cartera") {
        const ordenCartera = { D: 4, C: 3, B: 2, A: 1, COLA_FINAL: 0.5, AL_CORRIENTE: 0 };
        return (ordenCartera[b.categoria] || 0) - (ordenCartera[a.categoria] || 0);
      }

      return Number(b.mesesAtraso || 0) - Number(a.mesesAtraso || 0);
    });

    return filtered;
  }, [registros, busqueda, filtroRiesgo, filtroCartera, fraccionamiento, orden]);

  const exportarAgendaExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Agenda vencida", {
        views: [{ state: "frozen", ySplit: 4 }],
      });

      const hoy = new Date();
      const fechaTexto = hoy.toLocaleDateString("es-MX");

      worksheet.mergeCells("A1:K1");
      worksheet.getCell("A1").value = "AGENDA VENCIDA DE COBRANZA";
      worksheet.getCell("A1").font = {
        bold: true,
        size: 16,
        color: { argb: "FFFFFFFF" },
      };
      worksheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      worksheet.getCell("A1").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "8B0000" },
      };

      worksheet.mergeCells("A2:K2");
      worksheet.getCell("A2").value = `Fecha de exportación: ${fechaTexto}`;

      worksheet.mergeCells("A3:K3");
      worksheet.getCell("A3").value =
        `Filtro de riesgo: ${filtroRiesgo} | Filtro de cartera: ${filtroCartera} | Registros: ${registrosFiltrados.length}`;

      const headerRow = worksheet.getRow(4);
      headerRow.values = [
        "Meses atraso",
        "Riesgo",
        "Cartera",
        "Cliente",
        "Responsable",
        "Mensualidad",
        "Saldo atrasado",
        "Saldo restante",
        "Fraccionamiento",
        "Referencia",
        "Teléfono",
      ];
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "C00000" },
      };

      registrosFiltrados.forEach((item) => {
        const row = worksheet.addRow([
          item.mesesAtraso ?? 0,
          item.riesgo,
          item.categoria || "",
          item.cliente || "",
          item.responsableNombre || "",
          Number(item.mensualidad || 0),
          Number(item.saldoAtrasado || 0),
          Number(item.saldoRestante || 0),
          item.fraccionamiento || "",
          item.referencia || "",
          item.telefono || "",
        ]);

        row.getCell(6).numFmt = "$#,##0.00;[Red]-$#,##0.00";
        row.getCell(7).numFmt = "$#,##0.00;[Red]-$#,##0.00";
        row.getCell(8).numFmt = "$#,##0.00;[Red]-$#,##0.00";
      });

      worksheet.columns = [
        { width: 14 },
        { width: 12 },
        { width: 12 },
        { width: 28 },
        { width: 24 },
        { width: 14 },
        { width: 16 },
        { width: 16 },
        { width: 22 },
        { width: 18 },
        { width: 18 },
      ];

      worksheet.autoFilter = { from: "A4", to: "K4" };

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Agenda_Vencida_${fechaTexto.replaceAll("/", "-")}.xlsx`
      );
    } catch (error) {
      console.error("Error exportando Excel:", error);
      notify.error("No se pudo exportar el archivo Excel.");
    }
  };

  return (
    <div className="agenda-vencida-page">
      <div className="agenda-vencida-header">
        <div>
          <h2>Agenda Vencida</h2>
          <p>Cartera vencida real por meses atrasados y saldo vencido.</p>
        </div>

        <div className="agenda-vencida-top-actions">
          <button className="av-btn av-btn-light" onClick={() => navigate("/app/agenda")}>
            Volver a agenda del día
          </button>

          <button
            className="av-btn av-btn-light"
            onClick={() => navigate("/app/agenda/previsoria")}
          >
            Ver agenda previsoria
          </button>
          <button
  className="btn-pago btn-previsoria"
  onClick={() => navigate("/app/cobranza/inteligente")}
>
  Panel Inteligente
</button>

          <button className="av-btn av-btn-dark" onClick={exportarAgendaExcel}>
            Exportar Excel
          </button>
        </div>
      </div>

      <div className="agenda-vencida-summary">
        <div className="av-kpi av-kpi-high">
          <span>Riesgo alto</span>
          <strong>{resumen.altoCount}</strong>
          <small>3+ meses</small>
        </div>

        <div className="av-kpi av-kpi-medium">
          <span>Riesgo medio</span>
          <strong>{resumen.medioCount}</strong>
          <small>2 meses</small>
        </div>

        <div className="av-kpi av-kpi-low">
          <span>Riesgo bajo</span>
          <strong>{resumen.bajoCount}</strong>
          <small>1 mes</small>
        </div>

        <div className="av-kpi av-kpi-total">
          <span>Saldo atrasado</span>
          <strong>{agendaCobranza.formatMoney(resumen.saldoAtrasadoTotal)}</strong>
          <small>
            Saldo restante: {agendaCobranza.formatMoney(resumen.saldoRestanteTotal)}
          </small>
        </div>
      </div>

      <div
        className="agenda-vencida-summary"
        style={{ marginTop: 0, marginBottom: 18 }}
      >
        <div className="av-kpi">
          <span>Cartera A</span>
          <strong>{resumen.carteraACount}</strong>
          <small>1 a 3 meses</small>
        </div>

        <div className="av-kpi">
          <span>Cartera B</span>
          <strong>{resumen.carteraBCount}</strong>
          <small>4 a 6 meses</small>
        </div>

        <div className="av-kpi">
          <span>Cartera C</span>
          <strong>{resumen.carteraCCount}</strong>
          <small>7 a 9 meses</small>
        </div>

        <div className="av-kpi">
          <span>Cartera D</span>
          <strong>{resumen.carteraDCount}</strong>
          <small>10+ meses</small>
        </div>
      </div>

      <div className="agenda-vencida-controls">
        <div className="av-tabs">
          <button
            className={filtroRiesgo === "todos" ? "active" : ""}
            onClick={() => setFiltroRiesgo("todos")}
          >
            Todos
          </button>
          <button
            className={filtroRiesgo === "alto" ? "active" : ""}
            onClick={() => setFiltroRiesgo("alto")}
          >
            Alto
          </button>
          <button
            className={filtroRiesgo === "medio" ? "active" : ""}
            onClick={() => setFiltroRiesgo("medio")}
          >
            Medio
          </button>
          <button
            className={filtroRiesgo === "bajo" ? "active" : ""}
            onClick={() => setFiltroRiesgo("bajo")}
          >
            Bajo
          </button>
        </div>

        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar cliente, responsable, teléfono, referencia..."
        />

        <select value={filtroCartera} onChange={(e) => setFiltroCartera(e.target.value)}>
          <option value="todos">Todas las carteras</option>
          <option value="A">Cartera A</option>
          <option value="B">Cartera B</option>
          <option value="C">Cartera C</option>
          <option value="D">Cartera D</option>
          <option value="COLA_FINAL">Cola final</option>
        </select>

        <select value={fraccionamiento} onChange={(e) => setFraccionamiento(e.target.value)}>
          <option value="todos">Todos los fraccionamientos</option>
          {fraccionamientos.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>

        <select value={orden} onChange={(e) => setOrden(e.target.value)}>
          <option value="meses">Ordenar por meses de atraso</option>
          <option value="cartera">Ordenar por cartera</option>
          <option value="saldoAtrasado">Ordenar por saldo atrasado</option>
          <option value="saldoRestante">Ordenar por saldo restante</option>
          <option value="cliente">Ordenar por cliente</option>
        </select>
      </div>

      <div className="agenda-vencida-table-wrap">
        <table className="agenda-vencida-table">
          <thead>
            <tr>
              <th>Meses atraso</th>
              <th>Riesgo</th>
              <th>Cartera</th>
              <th>Cliente</th>
              <th>Responsable</th>
              <th>Mensualidad</th>
              <th>Saldo atrasado</th>
              <th>Saldo restante</th>
              <th>Venció</th>
              <th>Fraccionamiento</th>
              <th>Referencia</th>
              <th>Teléfono</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="13" className="av-empty">
                  Cargando agenda vencida...
                </td>
              </tr>
            ) : registrosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="13" className="av-empty">
                  No hay registros vencidos para los filtros actuales.
                </td>
              </tr>
            ) : (
              registrosFiltrados.map((item) => (
                <tr key={item.id}>
                  <td className="av-strong">{item.mesesAtraso}</td>

                  <td>
                    <span className={`av-risk av-risk-${item.riesgo}`}>{item.riesgo}</span>
                  </td>

                  <td>
                    <span className={`av-cartera av-cartera-${item.categoria?.toLowerCase()}`}>
                      {item.categoria}
                    </span>
                    <div className="av-cartera-desc">
                      {item.categoriaDescripcion}
                    </div>
                  </td>

                  <td className="av-strong">{item.cliente}</td>
                  <td>{item.responsableNombre || "—"}</td>
                  <td>{agendaCobranza.formatMoney(item.mensualidad)}</td>
                  <td>{agendaCobranza.formatMoney(item.saldoAtrasado)}</td>
                  <td>{agendaCobranza.formatMoney(item.saldoRestante)}</td>
                  <td>{agendaCobranza.formatDate(item.nextDueDate)}</td>
                  <td>{item.fraccionamiento || "—"}</td>
                  <td>{item.referencia || "—"}</td>
                  <td>{item.telefono || "—"}</td>
                  <td className="av-actions-cell">
                    <button className="av-mini av-view" onClick={() => handleVerDetalle(item)}>
                      Ver detalle
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgendaVencida;
